import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import LanguageToggle from "../components/LanguageToggle";

function Login({ onLogin, sessionExpired }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(
    sessionExpired ? t("login.sessionExpired") : "",
  );
  const [loading, setLoading] = useState(false);
  const [submitEnabled, setSubmitEnabled] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setSubmitEnabled(true), 800);
    return () => clearTimeout(timer);
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!submitEnabled) return;
    if (!email.trim() || !password.trim()) return;
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/login`,
        { email, password },
      );
      onLogin(res.data.token, res.data.user, res.data.legal);
    } catch (err) {
      setError(t("login.invalidCredentials"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#020817",
        backgroundImage:
          "radial-gradient(ellipse at 20% 50%, rgba(0,128,255,0.08) 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, rgba(0,245,255,0.06) 0%, transparent 50%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Segoe UI', sans-serif",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background grid */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(0,245,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,245,255,0.03) 1px, transparent 1px)",
          backgroundSize: "50px 50px",
          pointerEvents: "none",
        }}
      />

      {/* Glow orbs */}
      <div
        style={{
          position: "absolute",
          top: "15%",
          left: "10%",
          width: "300px",
          height: "300px",
          background:
            "radial-gradient(circle, rgba(0,128,255,0.06) 0%, transparent 70%)",
          borderRadius: "50%",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "20%",
          right: "10%",
          width: "250px",
          height: "250px",
          background:
            "radial-gradient(circle, rgba(0,245,255,0.05) 0%, transparent 70%)",
          borderRadius: "50%",
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: "420px",
          padding: "0 24px",
        }}
      >
        {/* LOGO */}
        <div style={{ textAlign: "center", marginBottom: "36px" }}>
          <img
            src="/rigiq-logo.png"
            alt="RigIQ"
            style={{
              width: "220px",
              height: "auto",
              marginBottom: "12px",
              filter: "drop-shadow(0 0 24px rgba(0,245,255,0.35))",
              mixBlendMode: "screen",
            }}
          />
          <div
            style={{
              color: "rgba(0,245,255,0.5)",
              fontSize: "10px",
              letterSpacing: "4px",
              textTransform: "uppercase",
            }}
          >
            {t("common.tagline")}
          </div>
        </div>

        {/* LANGUAGE TOGGLE */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: "20px" }}>
          <LanguageToggle />
        </div>

        {/* LOGIN CARD */}
        <div
          style={{
            backgroundColor: "rgba(2,8,23,0.9)",
            border: "1px solid rgba(0,245,255,0.15)",
            borderRadius: "12px",
            padding: "32px",
            backdropFilter: "blur(20px)",
            boxShadow:
              "0 0 60px rgba(0,245,255,0.06), inset 0 1px 0 rgba(0,245,255,0.1)",
          }}
        >
          <div
            style={{
              color: "rgba(0,245,255,0.4)",
              fontSize: "10px",
              letterSpacing: "3px",
              textAlign: "center",
              marginBottom: "28px",
              fontWeight: "700",
            }}
          >
            {t("login.secureLogin")}
          </div>

          <form onSubmit={handleSubmit} autoComplete="off">
            {/* EMAIL */}
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  color: "#00f5ff",
                  fontSize: "10px",
                  letterSpacing: "2px",
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "700",
                }}
              >
                {t("login.emailAddress")}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError("");
                }}
                required
                autoComplete="off"
                placeholder="operator@company.com"
                style={{
                  width: "100%",
                  backgroundColor: "rgba(0,245,255,0.05)",
                  border: "1px solid rgba(0,245,255,0.2)",
                  borderRadius: "6px",
                  padding: "11px 14px",
                  color: "white",
                  fontSize: "14px",
                  outline: "none",
                  boxSizing: "border-box",
                  transition: "border-color 0.2s",
                }}
                onFocus={(e) =>
                  (e.target.style.borderColor = "rgba(0,245,255,0.5)")
                }
                onBlur={(e) =>
                  (e.target.style.borderColor = "rgba(0,245,255,0.2)")
                }
              />
            </div>

            {/* PASSWORD */}
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  color: "#00f5ff",
                  fontSize: "10px",
                  letterSpacing: "2px",
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "700",
                }}
              >
                {t("login.password")}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError("");
                }}
                required
                autoComplete="new-password"
                placeholder="••••••••"
                style={{
                  width: "100%",
                  backgroundColor: "rgba(0,245,255,0.05)",
                  border: "1px solid rgba(0,245,255,0.2)",
                  borderRadius: "6px",
                  padding: "11px 14px",
                  color: "white",
                  fontSize: "14px",
                  outline: "none",
                  boxSizing: "border-box",
                  transition: "border-color 0.2s",
                }}
                onFocus={(e) =>
                  (e.target.style.borderColor = "rgba(0,245,255,0.5)")
                }
                onBlur={(e) =>
                  (e.target.style.borderColor = "rgba(0,245,255,0.2)")
                }
              />
            </div>

            {/* FORGOT PASSWORD LINK — public sign-up is closed (accounts
                come from platform provisioning or an org admin adding a
                teammate), so this is the only link here now. */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: "20px",
              }}
            >
              <a
                href="/forgot-password"
                style={{
                  color: "rgba(0,245,255,0.4)",
                  fontSize: "11px",
                  letterSpacing: "1px",
                  textDecoration: "none",
                  transition: "color 0.2s",
                }}
                onMouseOver={(e) => (e.target.style.color = "#00f5ff")}
                onMouseOut={(e) =>
                  (e.target.style.color = "rgba(0,245,255,0.4)")
                }
              >
                {t("login.forgotPassword")}
              </a>
            </div>

            {/* ERROR */}
            {error && (
              <div
                style={{
                  backgroundColor: "rgba(255,68,102,0.1)",
                  border: "1px solid rgba(255,68,102,0.3)",
                  borderRadius: "6px",
                  padding: "10px 14px",
                  marginBottom: "20px",
                  color: "#ff4466",
                  fontSize: "12px",
                  textAlign: "center",
                  letterSpacing: "1px",
                }}
              >
                ⚠ {error}
              </div>
            )}

            {/* SUBMIT */}
            <button
              type="submit"
              disabled={loading || !submitEnabled}
              style={{
                width: "100%",
                background: loading
                  ? "rgba(0,245,255,0.2)"
                  : "linear-gradient(135deg, #00f5ff, #0080ff)",
                color: loading ? "rgba(0,245,255,0.5)" : "#020817",
                fontWeight: "700",
                fontSize: "12px",
                letterSpacing: "3px",
                padding: "13px",
                borderRadius: "6px",
                border: "none",
                cursor: loading ? "not-allowed" : "pointer",
                boxShadow: loading ? "none" : "0 0 20px rgba(0,245,255,0.25)",
                transition: "all 0.2s",
              }}
            >
              {loading ? t("login.authenticating") : t("login.enterPlatform")}
            </button>
          </form>
        </div>

        {/* FOOTER */}
        <div style={{ textAlign: "center", marginTop: "28px" }}>
          <div
            style={{
              color: "rgba(255,255,255,0.2)",
              fontSize: "10px",
              letterSpacing: "2px",
              marginBottom: "6px",
            }}
          >
            {t("common.poweredBy")}
          </div>
          <div
            style={{
              color: "rgba(0,245,255,0.4)",
              fontSize: "11px",
              letterSpacing: "3px",
              fontWeight: "700",
            }}
          >
            TRI-M LLC
          </div>
          <div
            style={{
              color: "rgba(255,255,255,0.15)",
              fontSize: "9px",
              letterSpacing: "1px",
              marginTop: "16px",
            }}
          >
            {t("common.copyright")}
          </div>
        </div>
      </div>

      <style>{`
        input::placeholder { color: rgba(255,255,255,0.2); }
        input:-webkit-autofill {
          -webkit-box-shadow: 0 0 0 30px #020817 inset !important;
          -webkit-text-fill-color: white !important;
        }
      `}</style>
    </div>
  );
}

export default Login;
